import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLive } from "@/context/LiveContext";
import { StatusBadge } from "@/components/jarvis/StatusBadge";
import { useToolRunner } from "@/components/jarvis/useToolRunner";
import { Panel, Field, inputCls, Action, Note } from "./ui";

function TvPanel() {
  const { status } = useLive();
  const { run, dialog } = useToolRunner();
  const [tv, setTv] = useState(null);
  const load = () => api.get("/devices").then((r) => setTv(r.data.find((d) => d.kind === "tv") || { name: "Tele del cuarto", ip: "192.168.1.18", port: null, apps: [] }));
  useEffect(() => { load(); }, []);
  if (!tv) return null;
  const st = status("adb");
  const save = async () => { try { await api.put("/devices/tv", { name: tv.name, ip: tv.ip, port: tv.port ? Number(tv.port) : null }); toast.success("Tele guardada"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const connect = async () => {
    const s = await run("tv_connect", { ip: tv.ip, ...(tv.port ? { port: Number(tv.port) } : {}) });
    if (s?.status === "confirmada" && s.result?.port) await api.put("/devices/tv", { name: tv.name, ip: tv.ip, port: s.result.port });
  };
  const apps = async () => {
    const s = await run("tv_list_apps");
    if (s?.status === "confirmada") { await api.put("/devices/tv/apps", { apps: s.result.apps || [] }); load(); }
  };
  return (
    <Panel title="TELE DEL CUARTO · ADB" testId="tv-config-panel" right={<StatusBadge status={st.status} testId="tv-config-status" />}>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Nombre"><input className={inputCls} value={tv.name} onChange={(e) => setTv({ ...tv, name: e.target.value })} data-testid="tv-name-input" /></Field>
        <Field label="IP"><input className={inputCls} value={tv.ip} onChange={(e) => setTv({ ...tv, ip: e.target.value })} data-testid="tv-ip-input" /></Field>
        <Field label="Puerto (vacío = detectar)"><input className={inputCls} value={tv.port || ""} onChange={(e) => setTv({ ...tv, port: e.target.value })} data-testid="tv-port-input" placeholder="no verificado" /></Field>
      </div>
      <Note>{st.detail} · Puerto {tv.port_verified ? "verificado" : "no verificado"}. Microsonic · integración principal ADB por red desde el agente.</Note>
      <div className="mt-3 flex flex-wrap gap-2">
        <Action testId="tv-save-button" onClick={save}>Guardar</Action>
        <Action testId="tv-connect-button" variant="ghost" onClick={connect}>Conectar / detectar</Action>
        <Action testId="tv-status-button" variant="ghost" onClick={() => run("tv_status")}>Diagnóstico</Action>
        <Action testId="tv-apps-button" variant="ghost" onClick={apps}>Consultar apps instaladas</Action>
      </div>
      {tv.apps?.length > 0 && <div className="mt-3 flex flex-wrap gap-1">{tv.apps.map((a) => <span key={a.package} className="border border-cyan-500/20 px-1.5 py-0.5 text-[10px] text-slate-300">{a.label}</span>)}</div>}
      <Note>Suspender ≠ apagar: "sleep" deja la tele en reposo. Si al apagarse corta Wi-Fi/Ethernet, ADB no podrá encenderla; quedan el control remoto, HDMI-CEC o un enchufe inteligente.</Note>
      {dialog}
    </Panel>
  );
}

function EwelinkPanel() {
  const [st, setSt] = useState(null);
  const [devs, setDevs] = useState([]);
  const load = async () => {
    const s = (await api.get("/ewelink/status")).data;
    setSt(s);
    if (s.status === "conectado") api.get("/ewelink/devices").then((r) => setDevs(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { load(); }, []);
  const auth = async () => { try { window.location.href = (await api.get("/ewelink/authorize")).data.url; } catch (e) { toast.error(errMsg(e)); } };
  const meta = (d, k, v) => setDevs(devs.map((x) => (x.id === d.id ? { ...x, [k]: v } : x)));
  const saveMeta = (d) => api.put(`/ewelink/devices/${d.id}/meta`, { alias: d.alias, room: d.room }).then(() => toast.success("Alias guardado"));
  return (
    <Panel title="LUCES · eWeLink" testId="ewelink-panel" right={st && <StatusBadge status={st.status} testId="ewelink-status" />}>
      <Note>{st?.detail || st?.last_error || ""}</Note>
      <Note>Método: API abierta CoolKit v2 (OAuth 2.0). Requiere crear una app en dev.ewelink.cc y cargar EWELINK_APP_ID / EWELINK_APP_SECRET en el backend. Los tokens se guardan cifrados (Fernet) y se renuevan solos.</Note>
      <div className="mt-3 flex gap-2">
        <Action testId="ewelink-authorize-button" onClick={auth} disabled={st?.status === "pendiente" && st?.detail?.startsWith("Faltan")}>Autorizar cuenta</Action>
        {st?.status === "conectado" && <Action testId="ewelink-unlink-button" variant="danger" onClick={() => api.delete("/ewelink/authorization").then(load)}>Desvincular</Action>}
      </div>
      {devs.map((d) => (
        <div key={d.id} className="mt-2 grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center text-xs" data-testid={`light-row-${d.id}`}>
          <span className="text-slate-200">{d.name} <StatusBadge status={d.online ? "conectado" : "desconectado"} /></span>
          <input className={inputCls} placeholder="alias" value={d.alias} onChange={(e) => meta(d, "alias", e.target.value)} />
          <input className={inputCls} placeholder="habitación" value={d.room} onChange={(e) => meta(d, "room", e.target.value)} />
          <Action variant="ghost" onClick={() => saveMeta(d)}>Guardar</Action>
        </div>
      ))}
    </Panel>
  );
}

function AlexaPanel() {
  const [st, setSt] = useState(null);
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/alexa/status").then(r => setSt(r.data)).catch(e => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!code) return; const id = setTimeout(() => setCode(null), 600000); return () => clearTimeout(id); }, [code]);
  const pair = async () => { setBusy(true); try { setCode((await api.post("/alexa/pair")).data.code); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const unlink = async () => { setBusy(true); try { await api.delete("/alexa/link"); setCode(null); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return <Panel title="ALEXA · JARVIS" testId="alexa-panel" right={<StatusBadge status={st?.linked ? "conectado" : "pendiente"} />}>
    <Note>{st?.linked ? "Cuenta vinculada. Las órdenes respetan los permisos del panel y no se repiten si hay incertidumbre." : "Abrí la skill Jarvis en Alexa y vinculá tu cuenta con un código temporal."}</Note>
    <div className="mt-3 flex flex-wrap gap-2"><Action onClick={pair} disabled={busy}>Generar código</Action><Action variant="ghost" onClick={load}>Actualizar estado</Action>{st?.linked && <Action variant="danger" disabled={busy} onClick={unlink}>Desvincular Alexa</Action>}</div>
    {code && <div className="mt-3 text-cyan-200"><p className="text-2xl tracking-widest">{code}</p><Note>Vence en 10 minutos y sirve una vez. Decí: «Alexa, abre Jarvis», después «vincula código {code}».</Note></div>}
    <Note>Ejemplos: «estado de la PC» o «ejecuta prende luz». Si una acción necesita confirmación, se confirma desde este panel.</Note>
  </Panel>;
}

function OptionalModules() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/modules").then((r) => setM(r.data)); }, []);
  if (!m) return null;
  return (
    <Panel title="MÓDULOS OPCIONALES" testId="optional-modules">
      <div className="grid sm:grid-cols-2 gap-4 text-xs">
        <div data-testid="module-ps5"><div className="flex gap-2 items-center mb-2"><span className="font-hud text-cyan-200">PS5</span><StatusBadge status="pendiente" /></div>
          {m.ps5.capabilities.map((c) => <p key={c.name} className="text-slate-400 mb-1"><b className="text-slate-200">{c.name}</b> ({c.support}): {c.method}</p>)}</div>

      </div>
    </Panel>
  );
}

export default function DevicesTab() {
  return (
    <div>
      <TvPanel />
      <EwelinkPanel />
      <AlexaPanel />
      <Panel title="OBS WEBSOCKET" testId="obs-config-panel">
        <Note>La conexión a OBS es local: se configura en config.json del agente (host 127.0.0.1, puerto 4455, contraseña de Herramientas › Ajustes del servidor WebSocket). La contraseña nunca sale de su PC. Las escenas se consultan en vivo; Jarvis no inventa nombres.</Note>
      </Panel>
      <OptionalModules />
    </div>
  );
}
